import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ArticleTemplate, EditEventType } from '../../_models';
import { RfidLabelType, RfidTagging } from '../../_models/article-template.model';
import { FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";

import { ActivatedRoute, Router } from '@angular/router'
import { ArticleTemplateService } from '../../_services'
import { EditInputComponent } from '../_shared/edit-input/edit-input.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@ng-select/ng-select';
import { packageDimensionValidator, packageWeightValidator } from '../../_validators/mac-address.validator';
import { TechnicianStockLevelsGridComponent } from '../technician-stock-levels/technician-stock-levels-grid.component';
import { TechnicianStockLevelDraft, TechnicianStockLevelService } from '../../_services/technician-stock-level.service';
import { ModalService } from '../../_services/modal.service';
import { httpErrorMessage } from '../../_helpers/http-error-message';
import { normalizeShelfLabel } from '../../_helpers/shelf-label';
import { normalizeXid, xidToEpc } from '../../_helpers/xid';
import { XidRekeyDialogComponent } from '../_shared/xid-rekey-dialog/xid-rekey-dialog.component';
import { SerialNumberPatternTesterComponent } from '../_shared/serial-number-pattern-tester/serial-number-pattern-tester.component';
import {
  SERIAL_NUMBER_PATTERN_MAX_LENGTH,
  normalizeSerialNumberPattern,
  serialNumberPatternError,
  serialNumberPatternValidator,
} from '../../_helpers/serial-number-pattern';
@Component({
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    EditInputComponent,
    NgSelectModule,
    FontAwesomeModule
  ,
    TechnicianStockLevelsGridComponent,
    SerialNumberPatternTesterComponent],
  selector: 'hawk-article-template-show',
  templateUrl: './article-template-show.component.html',
  styleUrls: ['./article-template-show.component.scss'],
  host: {
    class: 'app-page',
  }
})
export class ArticleTemplateShowComponent implements OnInit {
  public articleTemplateId: string | string | number
  public newArticleTemplate: boolean = false
  public submittingTemplate: boolean = false
  public articleTemplate: ArticleTemplate;
  public articleTemplateForm: FormGroup;
  /** True, während ein Artikelbild hoch- oder heruntergeladen wird */
  public uploadingImage: boolean = false
  /** Verstecktes File-Input für den Artikelbild-Upload */
  @ViewChild('imageInput') private imageInput: ElementRef<HTMLInputElement> | undefined

  /** Bestände aus dem Anlegen-Dialog — tragen noch keine Vorlagen-ID. */
  public stockLevelDrafts: TechnicianStockLevelDraft[] = [];

  public badPartHandlingOptions = [
    { name: 'Keine', id: null },
    { name: 'Reparatur: SPC', id: 'SPC' },
    { name: 'Reparatur: PartnerTECH', id: 'PartnerTECH' },
    { name: 'Reparatur: METTLER', id: 'METTLER' },
    // Umdasch-Bad-Parts werden im Warehouse-App automatisch ins
    // eigene Umdasch-Zwischenlager eingebucht (Repair-Center-Config)
    { name: 'Reparatur: UMDASCH', id: 'UMDASCH' },
    { name: 'Entsorgung', id: 'DISPOSAL' },
  ]

  // RFID-Etiketten (bentomax/hawk#605)
  public rfidTaggingOptions: { name: string, id: RfidTagging }[] = [
    { name: 'Kein', id: 'none' },
    { name: 'Einzeln', id: 'individual' },
    { name: 'Gruppe', id: 'group' },
  ]

  public rfidLabelTypeOptions: { name: string, id: RfidLabelType }[] = [
    { name: 'On-Metal', id: 'on_metal' },
    { name: 'On-Plastic', id: 'on_plastic' },
  ]

  /**
   * Gewählter Etikett-Modus, der erst zusammen mit dem Etikettentyp gespeichert
   * werden kann (die DB verlangt beides gleichzeitig).
   */
  public pendingRfidTagging: RfidTagging | null = null

  // SN-Muster (bentomax/hawk#611); Hinweis als Property, weil „<sn>" im Template als Tag gälte
  public readonly serialNumberPatternMaxLength = SERIAL_NUMBER_PATTERN_MAX_LENGTH
  public readonly serialNumberPatternHint =
    'Regulärer Ausdruck für QR-Codes, die mehr als die Seriennummer enthalten. ' +
    'Die SN steht in (?<sn>…). Beispiel: 01750338654(?<sn>\\w+). Ohne Lookarounds und Rückverweise.'
  /** Entwurf beim Bearbeiten, damit „Scan testen" schon vor dem Speichern greift */
  public serialNumberPatternDraft = ''

  public editingProps = {
    description: false,
    product_category: false,
    vendor: false,
    part_nr: false,
    end_of_life: false,
    sn: false,
    comment1: false,
    comment2: false,
    comment3: false,
    eichamtsrelevant: false,
    default_owner_bentomax: false,
    bad_part_handling: false,
    info: false,
    purchase_value: false,
    selling_value: false,
    warning_threshold: false,
    minimum_stock: false,
    unidata_report: false,
    unidatahdd: false,
    unidata_reinstall: false,
    active: false,
    // Neue Felder für Paketabmessungen
    package_height: false,
    package_width: false,
    package_length: false,
    package_weight: false,
    // Neue Felder für Bestellinformationen
    order_link: false,
    order_number: false,
    // RFID-Etiketten
    rfid_tagging: false,
    rfid_label_type: false,
    group_xid: false,
    group_shelf_label: false,
    serial_number_pattern: false
  }

  // Column Definitions: Defines the columns to be displayed.
  constructor(
    private articleTemplateService: ArticleTemplateService,
    private stockLevelService: TechnicianStockLevelService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: ModalService
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.articleTemplateId = params.get('id')
      if(Number(this.articleTemplateId) > 0) {
        this.newArticleTemplate = false;
        this.loadArticleTemplate()
      } else {
        this.newArticleTemplate = true;
        this.articleTemplateForm = new FormGroup({
          description: new FormControl(null, Validators.required),
          product_category: new FormControl(null, Validators.required),
          vendor: new FormControl(null, Validators.required),
          part_nr: new FormControl(),
          end_of_life: new FormControl(false),
          sn: new FormControl(true),
          comment1: new FormControl(''),
          comment2: new FormControl(''),
          comment3: new FormControl(''),
          eichamtsrelevant: new FormControl(false),

          default_owner_bentomax: new FormControl(false),
          bad_part_handling: new FormControl(null),
          info: new FormControl(''),
          purchase_value: new FormControl(),
          selling_value: new FormControl(),
          warning_threshold: new FormControl(),
          minimum_stock: new FormControl(),
          unidata_report: new FormControl(false),
          unidatahdd: new FormControl(false),
          unidata_reinstall: new FormControl(false),
          
          // Neue Felder für Paketabmessungen
          package_height: new FormControl(null, packageDimensionValidator()),
          package_width: new FormControl(null, packageDimensionValidator()),
          package_length: new FormControl(null, packageDimensionValidator()),
          package_weight: new FormControl(null, packageWeightValidator()),
          
          // Neue Felder für Bestellinformationen
          order_link: new FormControl(''),
          order_number: new FormControl(''),

          // RFID-Etiketten: Etikettentyp nur Pflicht, wenn getaggt wird
          rfid_tagging: new FormControl<RfidTagging>('none', Validators.required),
          rfid_label_type: new FormControl<RfidLabelType | null>(null),

          // SN-Muster für Scans (leer = keins)
          serial_number_pattern: new FormControl<string | null>(null, serialNumberPatternValidator())
        });
        this.initRfidFormRules();
      }
    });
  }

  private loadArticleTemplate() {
    this.articleTemplateService.getArticleTemplate(this.articleTemplateId).subscribe(
      {
        next: (response) => {
          this.articleTemplate = response;
        },
        error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  public onDelete() {
    if(confirm('Möchten Sie die Artikelvorlage wirklich löschen?')) {
      this.articleTemplateService.delete(this.articleTemplateId).subscribe(
        {
          next: () => {
            this.router.navigate(['/', 'artikelvorlagen', 'uebersicht']);
          },
          error: (error) => {
            alert(error?.message || 'Something wrong went');
          }
        }
      )
    }
    return false;
  }

  public updateKey(field: string, event: EditEventType) {
    this.metaUpdate(field, event)
  }

  /** Versand im eigenen Herstellerkarton; bei Ablehnung durch Rails springt der Haken zurück */
  public updateShipsInOwnBox(checkbox: HTMLInputElement) {
    const wanted = checkbox.checked;
    this.metaUpdate('ships_in_own_box', wanted, {}, undefined, () => {
      checkbox.checked = !!this.articleTemplate?.ships_in_own_box;
    });
  }

  private metaUpdate(key = null, value = null, data = {}, onSuccess?: () => void, onError?: () => void) {
    const changeData = key === null ? data : {}
    if(key !== null)
      changeData[key] = value
    this.articleTemplateService.update(this.articleTemplateId, { article_template: changeData })
    .subscribe(
      {
        next: (response) => {
          this.articleTemplate = response;
          if(key)
            this.editingProps[key] = false;
          onSuccess?.();
        },
        error: (error) => {
          console.log('error', error);
          // Validierungsfehler (z. B. doppelte oder gesperrte XID) sichtbar machen
          alert(httpErrorMessage(error, 'Die Änderung konnte nicht gespeichert werden.'));
          onError?.();
        }
      }
    )
  }

  // New template

  public createArticleTemplate() {
    const params = {
      ...this.articleTemplateForm.value,
      serial_number_pattern: normalizeSerialNumberPattern(this.articleTemplateForm.value.serial_number_pattern),
    };
    this.submittingTemplate = true;
    this.articleTemplateService.create({ article_template: params }).subscribe(
      {
        next: async (response) => {
          await this.createStockLevels(response.id);
          this.submittingTemplate = false;
          this.router.navigate(['/', 'artikelvorlagen', response.id]);
        },
        error: (error) => {
          console.log('error', error);
          alert(`Please fix errors: ${error}`);
          this.submittingTemplate = false;
        }
      }
    )
  }

  /**
   * Speichert die im Anlegen-Dialog vorgemerkten Bestände, sobald die Vorlage
   * eine ID hat. Ein Fehler bricht das Anlegen NICHT ab — die Vorlage steht
   * bereits, die Bestände lassen sich nachtragen.
   */
  private async createStockLevels(templateId: number): Promise<void> {
    if (this.stockLevelDrafts.length === 0) return;

    try {
      await this.stockLevelService.createMany(
        this.stockLevelDrafts.map(draft => ({ ...draft, article_template_id: templateId }))
      );
    } catch (error) {
      alert(`Vorlage angelegt, aber die Bestände konnten nicht gespeichert werden: ${
        error instanceof Error ? error.message : 'unbekannter Fehler'}`);
    }
  }

  public badPartLabel(badPartHandlingId: string | null): string {
    const option = this.badPartHandlingOptions.find(option => option.id === badPartHandlingId);
    return option ? option.name : this.badPartHandlingOptions[0].name;
  }

  // --- RFID-Etiketten ---

  /** Im Anlegen-Formular: Etikettentyp-Pflicht und „Gruppe nur ohne SN" durchsetzen */
  private initRfidFormRules() {
    const tagging = this.articleTemplateForm.controls['rfid_tagging'];
    const labelType = this.articleTemplateForm.controls['rfid_label_type'];
    const sn = this.articleTemplateForm.controls['sn'];

    tagging.valueChanges.subscribe((value: RfidTagging) => {
      if (value === 'none') {
        labelType.setValue(null);
        labelType.clearValidators();
      } else {
        labelType.setValidators(Validators.required);
      }
      labelType.updateValueAndValidity();
    });

    sn.valueChanges.subscribe((withSn: boolean) => {
      if (withSn && tagging.value === 'group') {
        tagging.setValue('none');
      }
    });
  }

  public rfidTaggingLabel(value: RfidTagging | null | undefined): string {
    return this.rfidTaggingOptions.find(option => option.id === value)?.name ?? this.rfidTaggingOptions[0].name;
  }

  public rfidLabelTypeLabel(value: RfidLabelType | null | undefined): string {
    return this.rfidLabelTypeOptions.find(option => option.id === value)?.name ?? '-';
  }

  /** „Gruppe" gibt es nur für Vorlagen ohne Seriennummer */
  public rfidTaggingOptionsFor(sn: boolean) {
    return sn ? this.rfidTaggingOptions.filter(option => option.id !== 'group') : this.rfidTaggingOptions;
  }

  /** Etikettentyp anzeigen, sobald getaggt wird (oder gerade umgestellt wird) */
  public get showRfidLabelType(): boolean {
    return this.pendingRfidTagging !== null || (this.articleTemplate?.rfid_tagging ?? 'none') !== 'none';
  }

  /** EPC des Gruppen-Etiketts; fehlt das Server-Feld, aus der XID gerechnet */
  public get groupEpc(): string | null {
    return this.articleTemplate?.group_epc || xidToEpc(this.articleTemplate?.group_xid);
  }

  public updateRfidTagging(value: EditEventType) {
    const tagging = value as RfidTagging;
    this.editingProps.rfid_tagging = false;
    this.pendingRfidTagging = null;
    if (!tagging || tagging === this.articleTemplate.rfid_tagging) return;

    if (tagging === 'none') {
      // Ohne Etiketten darf kein Etikettentyp gesetzt sein
      this.metaUpdate(null, null, { rfid_tagging: 'none', rfid_label_type: null });
    } else if (this.articleTemplate.rfid_label_type) {
      this.metaUpdate('rfid_tagging', tagging);
    } else {
      // Erst den Etikettentyp abfragen, dann beides zusammen speichern
      this.pendingRfidTagging = tagging;
      this.editingProps.rfid_label_type = true;
    }
  }

  public updateRfidLabelType(value: EditEventType) {
    const labelType = value as RfidLabelType;
    if (!labelType) return;

    const data: Record<string, string> = { rfid_label_type: labelType };
    if (this.pendingRfidTagging) {
      data['rfid_tagging'] = this.pendingRfidTagging;
    }
    this.metaUpdate(null, null, data, () => {
      this.pendingRfidTagging = null;
      this.editingProps.rfid_label_type = false;
    });
  }

  public cancelRfidLabelType() {
    this.pendingRfidTagging = null;
    this.editingProps.rfid_label_type = false;
  }

  /** Bearbeiten nur, solange noch keine Gruppen-XID vergeben ist */
  public toggleGroupXidEdit() {
    if (this.articleTemplate.group_xid) return;
    this.editingProps.group_xid = !this.editingProps.group_xid;
  }

  /**
   * Erstvergabe der Gruppen-XID. Eine vorhandene XID wird nur über
   * „Umschlüsseln" geändert, damit die alte in die Sperrliste kommt.
   */
  public updateGroupXid(value: EditEventType) {
    const input = typeof value === 'string' ? value.trim() : '';
    if (!input) {
      this.editingProps.group_xid = false;
      return;
    }
    const xid = normalizeXid(input);
    if (!xid) {
      alert('Ungültige XID. Erwartet wird XID-NNNNNNN (7 Ziffern) oder ein Bentomax-EPC.');
      return;
    }
    this.metaUpdate('group_xid', xid);
  }

  /** Gruppen-Lagerort normalisiert speichern; leer löscht ihn */
  public updateGroupShelfLabel(value: EditEventType) {
    this.metaUpdate('group_shelf_label', normalizeShelfLabel(typeof value === 'string' ? value : null));
  }

  public openRekeyDialog() {
    const ref = this.modalService.open(XidRekeyDialogComponent, {
      inputs: {
        ownerType: 'article_template',
        ownerId: Number(this.articleTemplateId),
        currentXid: this.articleTemplate.group_xid ?? null,
      },
      minHeight: 'auto',
    });
    ref.closed.subscribe((saved) => {
      if (saved === true) {
        this.loadArticleTemplate();
      }
    });
  }

  // --- SN-Muster (bentomax/hawk#611) ---

  public get serialNumberPatternDraftError(): string | null {
    return serialNumberPatternError(this.serialNumberPatternDraft)
  }

  public toggleSerialNumberPatternEdit() {
    if (this.editingProps.serial_number_pattern) {
      this.editingProps.serial_number_pattern = false
      return
    }
    this.serialNumberPatternDraft = this.articleTemplate.serial_number_pattern ?? ''
    this.editingProps.serial_number_pattern = true
  }

  /** Speichert das Muster; leer löscht es, ungültige Muster gehen nicht raus */
  public saveSerialNumberPattern() {
    if (this.serialNumberPatternDraftError) return
    const pattern = normalizeSerialNumberPattern(this.serialNumberPatternDraft)
    if (pattern === (this.articleTemplate.serial_number_pattern ?? null)) {
      this.editingProps.serial_number_pattern = false
      return
    }
    this.metaUpdate('serial_number_pattern', pattern)
  }

  // --- Artikelbild ---

  /** Öffnet den Dateiauswahl-Dialog für den Artikelbild-Upload */
  public openImageUpload() {
    this.imageInput?.nativeElement.click();
  }

  /** Lädt die ausgewählte Bilddatei hoch und aktualisiert die Vorlage */
  public onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Input zurücksetzen, damit dieselbe Datei erneut ausgewählt werden kann
    input.value = '';
    if (!file) {
      return;
    }

    this.uploadingImage = true;
    this.articleTemplateService.uploadImage(this.articleTemplateId, file).subscribe(
      {
        next: (response) => {
          this.articleTemplate.image_filename = response.filename;
          this.uploadingImage = false;
        },
        error: (error) => {
          console.log('error', error);
          alert('Beim Hochladen des Artikelbilds ist ein Fehler aufgetreten.');
          this.uploadingImage = false;
        }
      }
    );
  }

  /** Entfernt das Artikelbild der Vorlage nach Rückfrage */
  public onDeleteImage() {
    if (!this.articleTemplate.image_filename) {
      return;
    }
    if (!confirm('Möchten Sie das Artikelbild wirklich löschen?')) {
      return;
    }

    this.uploadingImage = true;
    this.articleTemplateService.deleteImage(this.articleTemplateId).subscribe(
      {
        next: () => {
          this.articleTemplate.image_filename = null;
          this.uploadingImage = false;
        },
        error: (error) => {
          console.log('error', error);
          alert('Beim Löschen des Artikelbilds ist ein Fehler aufgetreten.');
          this.uploadingImage = false;
        }
      }
    );
  }

  /** URL des hochgeladenen Artikelbilds */
  public articleImageUrl(): string {
    return this.articleTemplateService.getImageUrl(this.articleTemplateId, this.articleTemplate.image_filename!);
  }

}
